import React from "react";
import { Provider } from "react-redux";
import { ReviewsComponentTypes } from "tsi-common-react/src/apps/reviews/constants";
import { Dispatchers } from "tsi-common-react/src/apps/reviews/dispatchers";
import { readQueryParams } from "tsi-common-react/src/apps/reviews/history";
import {
    isoFormUUID,
    isoReviewsBrandID,
    isoReviewsProductTypeID,
    isoWebPageURL,
} from "tsi-common-react/src/models/nominals";
import { strToBool } from "tsi-common-react/src/utils/format";
import { notEmpty, unique } from "tsi-common-react/src/utils/functional";
import { dynamicPlaceComponent } from "tsi-common-react/src/utils/react";
import { v4 as uuidv4 } from "uuid";

import { store } from "../store";

/**
 * Init facet values from the URL
 */
const dispatchers = new Dispatchers(store.dispatch);
const initialFacetValues = readQueryParams();
if (initialFacetValues.product_type_id) {
    const productIDs = `${initialFacetValues.product_type_id}`
        .split(",")
        .map((ptid) => {
            return ptid
                ? isoReviewsProductTypeID.wrap(parseInt(ptid, 10))
                : null;
        })
        .filter(notEmpty)
        .filter(unique);
    if (productIDs.length) {
        dispatchers.setProductTypes(productIDs);
    }
}

delete initialFacetValues.product_type_id;
dispatchers.setFacetValues(initialFacetValues);

/**
 * Render main Reviews viewer component for reviews page
 */
dynamicPlaceComponent('[data-place-react="review-spa"]', async (elem) => {
    const { ReviewsViewer } = await import("./containers/ReviewsViewer");
    const brandID = isoReviewsBrandID.wrap(
        parseInt(elem.dataset.reviewsBrandId || "1", 10),
    );
    return (
        <Provider store={store}>
            <ReviewsViewer brandID={brandID} />
        </Provider>
    );
}).catch(console.error);

/**
 * Render "Write Review" application
 */
dynamicPlaceComponent('[data-place-react="write-review"]', async (elem) => {
    const { SingleProductWriteReviewForm } = await import(
        "tsi-common-react/src/apps/reviews/containers/SingleProductWriteReviewForm"
    );
    const brandID = isoReviewsBrandID.wrap(
        parseInt(elem.dataset.reviewsBrandId || "3", 10),
    );
    const brand = elem.dataset.brand || "Cocoon by Sealy";
    const returnURL = elem.dataset.returnUrl
        ? isoWebPageURL.wrap(elem.dataset.returnUrl)
        : undefined;
    return (
        <Provider store={store}>
            <SingleProductWriteReviewForm
                formUUID={isoFormUUID.wrap(uuidv4())}
                brandID={brandID}
                brand={brand}
                returnURL={returnURL}
            />
        </Provider>
    );
}).catch(console.error);

/**
 * Render PDP Reviews Application
 */
dynamicPlaceComponent('[data-place-react="product-reviews"]', async (elem) => {
    const { ProductReviews } = await import(
        "tsi-common-react/src/apps/reviews/containers/ProductReviews"
    );
    const brandID = isoReviewsBrandID.wrap(
        parseInt(elem.dataset.reviewsBrandId || "", 10),
    );
    const productUUIDs = JSON.parse(elem.dataset.reviewsProductUuids || "[]");
    const numReviews = parseInt(elem.dataset.numReviews || "", 10);
    const averageRating = parseFloat(elem.dataset.averageRating || "");
    const showProductsFilter = strToBool(
        elem.dataset.showProductsFilter || "no",
    );
    const showSourcesFilter = strToBool(elem.dataset.showSourcesFilter || "no");
    return (
        <Provider store={store}>
            <ProductReviews
                title={elem.dataset.reviewsTitle || ""}
                brandID={brandID}
                productUUIDs={productUUIDs}
                averageRating={averageRating}
                numReviews={numReviews}
                showProductsFilter={showProductsFilter}
                showSourcesFilter={showSourcesFilter}
                starHasStroke={true}
                reviewsComponentType={ReviewsComponentTypes.CAROUSEL}
                showWriteReviewButton={false}
                writeReviewFormUUID={isoFormUUID.wrap(uuidv4())}
                isCollapsedView={true}
            />
        </Provider>
    );
}).catch(console.error);
